import React, { useState, useEffect } from 'react'
import { Card, Row, Col, Typography, Space, Spin, message, Empty, Button } from 'antd'
import { 
  BarChartOutlined, 
  SettingOutlined,
  AppstoreAddOutlined,
  AppstoreOutlined,
  TruckOutlined
} from '@ant-design/icons'
import { useAuth } from '../../contexts/AuthContext'
import { useNavigate } from 'react-router-dom'
import { useTheme } from '../../contexts/ThemeContext'

const { Title, Text } = Typography

const MODULES = [
  {
    id: 'por-volume',
    title: 'Por volume',
    description: 'Volume a volume da nota',
    icon: 'AppstoreAddOutlined',
    color: '#5a6c7d',
    path: '/por-volume',
    checkPermission: (user) => {
      if (!user?.roles) return false
      const userRoles = user.roles.map(r => r.role.name)
      return userRoles.includes('inoperacional') || userRoles.includes('inmanager') || userRoles.includes('inadmin')
    }
  },
  {
    id: 'onboard',
    title: 'Onboard',
    description: 'Todos os volumes da nota',
    icon: 'AppstoreOutlined',
    color: '#5a6c7d',
    path: '/onboard',
    checkPermission: (user) => {
      if (!user?.roles) return false
      const userRoles = user.roles.map(r => r.role.name)
      return userRoles.includes('inonboard') || userRoles.includes('inmanager') || userRoles.includes('inadmin' || userRoles.includes('inoperaciona'))
    }
  },
  {
    id: 'painel-operacional',
    title: 'Painel Operacional',
    description: 'Dados e indicadores',
    icon: 'BarChartOutlined',
    color: '#5a6c7d',
    path: '/painel-operacional',
    checkPermission: (user) => {
      if (!user?.roles) return false
      const userRoles = user.roles.map(r => r.role.name)
      return userRoles.includes('inoperacional') || userRoles.includes('inexpedicao') || userRoles.includes('inmanager') || userRoles.includes('inadmin')
    }
  },
  {
    id: 'painel-administrativo',
    title: 'Painel Administrativo',
    description: '',
    icon: 'SettingOutlined',
    color: '#5a6c7d',
    path: '/painel-administrativo',
    checkPermission: (user) => {
      if (!user?.roles) return false
      const userRoles = user.roles.map(r => r.role.name)
      return userRoles.includes('inadmin')
    }
  }]

const ModulesMenu = () => {
  const [loading, setLoading] = useState(false)
  const { user, isLoading } = useAuth()
  const { currentTheme } = useTheme()
  const navigate = useNavigate()

  // Responsividade: detectar telas menores (<= 768px) e aplicar escala 0.75
  const [isSmallScreen, setIsSmallScreen] = useState(false)
  useEffect(() => {
    const mql = window.matchMedia('(max-width: 768px)')
    const update = () => setIsSmallScreen(mql.matches)
    update()
    mql.addEventListener('change', update)
    return () => mql.removeEventListener('change', update)
  }, [])
  const scale = isSmallScreen ? 0.75 : 1

  // Silenciar erro de extensío do Chrome relacionado a canais de mensagem fechados
  useEffect(() => {
    const handleUnhandledRejection = (event) => {
      const reason = event?.reason
      const message = typeof reason === 'string' ? reason : reason?.message
      if (
        typeof message === 'string' &&
        message.includes('A listener indicated an asynchronous response by returning true') &&
        message.includes('message channel closed')
      ) {
        event.preventDefault()
        // Opcional: log controlado para diagnóstico sem interromper a experiência
        console.warn('[Silenciado] Erro de extensío de mensagem assíncrona:', message)
      }
    }

    window.addEventListener('unhandledrejection', handleUnhandledRejection)
    return () => window.removeEventListener('unhandledrejection', handleUnhandledRejection)
  }, [])



  // Helper function para verificar permissões do usuário
  const hasPermission = (module) => {
    if (!user) return false
    
    const userRoles = user?.roles ? user.roles.map(r => r.role.name) : []
    
    return module.checkPermission(user)
  }

  // Ícones para os módulos (responsivos)
  const iconSize = `${48 * scale}px`
  const moduleIcons = {
    AppstoreAddOutlined: <AppstoreAddOutlined style={{ fontSize: iconSize, color: '#fff' }} />,
    AppstoreOutlined: <AppstoreOutlined style={{ fontSize: iconSize, color: '#fff' }} />,
    BarChartOutlined: <BarChartOutlined style={{ fontSize: iconSize, color: '#fff' }} />,
    SettingOutlined: <SettingOutlined style={{ fontSize: iconSize, color: '#fff' }} />,
    TruckOutlined: <TruckOutlined style={{ fontSize: iconSize, color: '#fff' }} />
  }

  const handleModuleClick = (module) => {
    // Verificar se o usuário tem permissío para acessar o módulo
    if (!hasPermission(module)) {
      message.error('Você não tem permissío para acessar este módulo')
      return
    }

    // Se for o painel administrativo e o usuário for admin, navegar para /admin
    if (module.id === 'painel-administrativo' && user?.roles?.some(r => r.role.name === 'inadmin')) {
      navigate('/admin')
    } else {
      navigate(module.path)
    }
  }

  // Filtrar módulos baseado nas permissões do usuário
  const availableModules = MODULES.filter(module => hasPermission(module))

  if (loading || isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '60vh' 
      }}>
        <Spin size="large" tip="Carregando módulos..." />
      </div>
    )
  }


  return (
    <div className="modules-menu-container" style={{ 
      padding: 'clamp(12px, 3vw, 24px)',
      background: currentTheme === 'dark' ? '#000000' : '#f5f5f5',
      minHeight: 'calc(100vh - 64px)'
    }}>
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <Title level={2} style={{ 
          margin: 0, 
          color: currentTheme === 'dark' ? '#ffffff' : '#1a1a1a',
          fontSize: `${24 * scale}px`
        }}>
          Menu de Módulos
        </Title>
        <Text style={{ 
          fontSize: `${16 * scale}px`, 
          color: currentTheme === 'dark' ? '#cccccc' : '#666' 
        }}>
          Bem-vindo, {user?.nome}! Selecione um módulo para começar.
        </Text>
      </div>

      {/* Módulos Grid */}
      <Row gutter={[24, 24]}>
        {availableModules.map((module) => (
          <Col 
            key={module.id}
            xs={24} 
            sm={12} 
            md={12} 
            lg={8} 
            xl={6}
          >
            <Card
              hoverable
              onClick={() => handleModuleClick(module)}
              style={{
                borderRadius: '12px',
                border: 'none',
                backgroundColor: currentTheme === 'dark' ? '#111111' : '#ffffff',
                boxShadow: currentTheme === 'dark' 
                  ? '0 2px 8px rgba(0, 0, 0, 0.3)' 
                  : '0 2px 8px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                height: `${200 * scale}px`
              }}
              styles={{
                body: {
                  padding: 'clamp(12px, 2.5vw, 24px)',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  alignItems: isSmallScreen ? 'center' : 'flex-start'
                }
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)'
                e.currentTarget.style.boxShadow = currentTheme === 'dark' 
                  ? '0 8px 24px rgba(0, 0, 0, 0.5)' 
                  : '0 8px 24px rgba(0, 0, 0, 0.15)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)'
                e.currentTarget.style.boxShadow = currentTheme === 'dark' 
                  ? '0 2px 8px rgba(0, 0, 0, 0.3)' 
                  : '0 2px 8px rgba(0, 0, 0, 0.1)'
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: isSmallScreen ? 'center' : 'flex-start', textAlign: isSmallScreen ? 'center' : 'left' }}>
                {/* Ícone */}
                <div 
                  style={{
                    width: `${80 * scale}px`,
                    height: `${80 * scale}px`,
                    borderRadius: '12px',
                    background: module.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '16px'
                  }}
                >
                  {moduleIcons[module.icon] || moduleIcons.SettingOutlined}
                </div>

                {/* Título */}
                <Title 
                  level={4} 
                  style={{ 
                    margin: '0 0 8px 0', 
                    color: currentTheme === 'dark' ? '#ffffff' : '#1a1a1a',
                    fontSize: `${18 * scale}px`,
                    fontWeight: '600'
                  }}
                >
                  {module.title}
                </Title>

                {/* Descrição */}
                <Text 
                  style={{ 
                    color: currentTheme === 'dark' ? '#cccccc' : '#666', 
                    fontSize: `${14 * scale}px`,
                    lineHeight: '1.4'
                  }}
                >
                  {module.description}
                </Text>
              </div>
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  )
}

export default ModulesMenu